import React, { useEffect, useRef, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ContactSection } from '../components/ContactSection';
import { motion } from 'framer-motion';
import { Wind, TrendingUp, MapPin, Waves, Sailboat, CheckCircle2, GraduationCap, Users, ShieldCheck, Package, Award, Coffee } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { captureUtmParams } from '../utils/utmTracking';
import { useNavigate } from 'react-router-dom';
const spotFeatures = [{
  icon: Wind,
  title: 'רוח צד (Side Shore)',
  description: 'תנאי רוח אידיאליים לגלישה בטוחה - הרוח נושבת לאורך החוף'
}, {
  icon: MapPin,
  title: 'מיקום מושלם בכנרת',
  description: 'חוף מעגן עדן - אחד החופים הטובים ביותר לספורט רוח'
}, {
  icon: Waves,
  title: 'מים שטוחים',
  description: 'מושלם ללמידה והתקדמות מהירה'
}];
const courses = [{
  title: 'קורס ווינג פויל למתחילים',
  description: 'לימוד מאפס, כולל תיאוריה ופרקטיקה על המים',
  icon: GraduationCap
}, {
  title: 'קורס מתקדמים',
  description: 'שיפור טכניקה, מהפכים ועוד',
  icon: TrendingUp
}, {
  title: 'הדרכות סאפ',
  description: 'שיעורי סאפ לכל הרמות - ממתחילים ועד מתקדמים',
  icon: Waves
}, {
  title: 'הדרכות קיאק וסרפסקי',
  description: 'לימוד חתירה מקצועית בקיאק ובסרפסקי',
  icon: Sailboat
}, {
  title: 'קבוצות סאפ',
  description: 'אימוני סאפ קבוצתיים באווירה כיפית ותומכת',
  icon: Users
}, {
  title: 'הדרכות אישיות',
  description: 'שיעור פרטי מותאם אישית לרמה שלך',
  icon: Users
}, {
  title: 'גלישת רוח (Windsurfing)',
  description: 'הדרכות לכל הרמות, ממתחילים ועד מתקדמים',
  icon: Sailboat
}];
const clubFeatures = [{
  title: 'שימוש חופשי בציוד חדיש ואיכותי',
  icon: ShieldCheck
}, {
  title: 'אחסון ציוד אישי במועדון',
  icon: Package
}, {
  title: 'מתקן לטליית ווינגים וקומפרסור',
  icon: Wind
}, {
  title: 'מדשאות נוחות להכנת ציוד',
  icon: MapPin
}, {
  title: 'מוזיקה ופינת קפה',
  icon: Coffee
}, {
  title: 'חניה קרובה למים',
  icon: CheckCircle2
}, {
  title: 'קהילת גולשים פעילה ותומכת',
  icon: Award
}];
function WindMeterWidget() {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // In RTL, scroll starts at the right. Scroll to left (0) to show the wind circle first.
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, []);
  return <div ref={scrollRef} dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto" data-id="element-2239" data-mp="335jk0lu60cnt">
      <div className="absolute inset-0 z-10" data-id="element-2240" data-mp="3d495eol9sf8t" />
      <iframe src="https://widget.holfuy.com/?station=2020&su=knots&te=C&lang=he&bg=ffffff" className="h-[320px] border-0" style={{
      minWidth: '520px',
      width: '100%'
    }} title="Wind Station - Beach Club Maagan" scrolling="no" data-id="element-2241" data-mp="2pfg38ae21b5r" />
    </div>;
}
export function WindMeterPage() {
  const navigate = useNavigate();
  useEffect(() => {
    captureUtmParams();
    document.title = 'מד רוח כנרת';
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'מד רוח כנרת בזמן אמת בחוף מעגן עדן. בדקו את תנאי הרוח לווינג פויל, גלישת רוח, סאפ וקיאק');
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'מד רוח כנרת, רוח כנרת, תנאי רוח כנרת, ווינג פויל כנרת, גלישת רוח כנרת, סאפ כנרת, חוף מעגן עדן');
    const ogTags = [{
      property: 'og:title',
      content: 'מד רוח כנרת'
    }, {
      property: 'og:description',
      content: 'מד רוח כנרת בזמן אמת בחוף מעגן עדן - מושלם לווינג פויל, גלישת רוח, סאפ וקיאק'
    }, {
      property: 'og:type',
      content: 'website'
    }, {
      property: 'og:url',
      content: window.location.href
    }];
    ogTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', tag.content);
    });
  }, []);
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-2242" data-mp="387k3572xe5pa">
      <Navigation data-id="element-2243" data-mp="385z6nbx1wcos" />

      <div className="relative h-[50vh] min-h-[400px] overflow-hidden bg-gradient-to-br from-[#0A5F8F] to-[#0D7AB8]" data-id="element-2244" data-mp="389n0ydabl34k">
        <div className="absolute inset-0 opacity-10" data-id="element-2245" data-mp="1som5nvwkfgol">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" data-id="element-2246" data-mp="1sp47r95wsp4f" />
        </div>
        <div className="relative z-10 container mx-auto px-4 h-full flex flex-col items-center justify-center text-center text-white" data-id="element-2247" data-mp="21gwep9vq5g49">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-2248" data-mp="26gsib80if5ja">
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md px-6 py-3 rounded-full mb-6 border border-white/30" data-id="element-2249" data-mp="2kfihct6w3faa">
              <Wind className="w-6 h-6" data-id="element-2250" data-mp="m1h3omkbwf2m" />
              <span className="font-medium text-lg" data-id="element-2251" data-mp="m2iecpxfmzjt">תנאי רוח בזמן אמת</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-id="element-2252" data-mp="q3ki9k2anzs4">
              מד רוח חוף מעגן - כנרת
            </h1>
            <p className="text-xl md:text-2xl font-light max-w-2xl mx-auto text-blue-100" data-id="element-2253" data-mp="1odtt66xzo8gz">
              בדקו את תנאי הרוח לפני שאתם יוצאים למים
            </p>
          </motion.div>
        </div>
      </div>

      <section className="py-16 bg-gray-50" data-id="element-2254" data-mp="1yb8as9vnf3av">
        <div className="container mx-auto px-4" data-id="element-2255" data-mp="1ya6p7bh0ayew">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-6xl mx-auto" data-id="element-2256" data-mp="2cb985fcpfsll">
            <div className="text-center mb-8" data-id="element-2257" data-mp="2h1zgn1gfgbki">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-4" data-id="element-2258" data-mp="2h4c5lyklrm72">
                מד רוח בזמן אמת
              </h2>
              <p className="text-gray-600 text-lg" data-id="element-2259" data-mp="2q37a96800o89">
                נתוני רוח, טמפרטורה ותנאי מזג אוויר מעודכנים כל דקה
              </p>
            </div>
            <div className="max-w-2xl mx-auto mb-6 space-y-3" data-id="element-2260" data-mp="39pimlge62fmx">
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-start gap-3 text-right" data-id="element-2261" data-mp="3eo28xhwo9tft">
                <Wind className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" data-id="element-2262" data-mp="30nhrap2hu3nw" />
                <p className="text-blue-800 text-sm font-medium" data-id="element-2263" data-mp="34ouyp3l8iewx">
                  ברוחות מערביות, בתוך המים יש כ-3-4 קשרים יותר ממה שמראה מד
                  הרוח.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-3 text-right" data-id="element-2264" data-mp="2vpzrbo6geys1">
                <span className="text-amber-500 shrink-0 mt-0.5" data-id="element-2265" data-mp="2h1mrgg48wvs9">📷</span>
                <p className="text-amber-800 text-sm font-medium" data-id="element-2266" data-mp="2m06g4oa84lob">
                  בקרוב — מצלמת חוף בזמן אמת!
                </p>
              </div>
            </div>
            <WindMeterWidget data-id="element-2267" data-mp="2cy6xxsnh3bjs" />
            <div dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto mt-6" data-id="element-2268" data-mp="2cxf3il5m3jv3">
              <div className="absolute inset-0 z-10" data-id="element-2269" data-mp="2niewid1n5j7h" />
              <iframe src="https://widget.holfuy.com/?mode=graphs&station=2020&su=knots" className="h-[580px] md:h-[850px] border-0" style={{
              minWidth: '520px',
              width: '100%'
            }} title="Wind Graphs - Beach Club Maagan" scrolling="no" data-id="element-2270" data-mp="2iiabqatpa2bg" />
            </div>
            <div dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto mt-6" data-id="element-2271" data-mp="1zrslqe095kts">
              <div className="absolute inset-0 z-10" data-id="element-2272" data-mp="1qr4jsho4vax9" />
              <iframe src="https://api.holfuy.com/archive/?s=2020" className="h-[600px] md:h-[700px] border-0" style={{
              minWidth: '520px',
              width: '100%'
            }} title="Wind Archive - Beach Club Maagan" scrolling="no" data-id="element-2273" data-mp="1qpt69q4vyosy" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gray-50" data-id="element-2274" data-mp="a8kks9xnt2r4">
        <div className="container mx-auto px-4" data-id="element-2275" data-mp="fa08k9r81nd7">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-2276" data-mp="f7ns61143l29">
            <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-600 px-4 py-2 rounded-full font-bold mb-4" data-id="element-2277" data-mp="3sga2w9tacmh5">
              <GraduationCap className="w-5 h-5" data-id="element-2278" data-mp="2v1frub2ag6jr" />
              <span data-id="element-2279" data-mp="2v0mir3fux4yg">ללמוד ולגלוש</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-2280" data-mp="2zz7gmw7mzcjq">
              קורסים והדרכות לכל הרמות
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-2281" data-mp="2azv9senco2ge">
              ממתחילים שעושים צעדים ראשונים ועד גולשים מתקדמים שרוצים לשפר
              טכניקה - המים השטוחים של הכנרת הופכים את הלמידה לקלה ובטוחה.
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-2282" data-mp="2p061tl0ebn35">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2283" data-mp="1rcfdgaj891k9">
              <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-2284" data-mp="260s5wxj3w2a0">
                ההדרכות שלנו
              </h3>
              <div className="space-y-6" data-id="element-2285" data-mp="18mfv06hw6kpt">
                {courses.map((course, index) => <div key={index} className="flex gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-2286" data-mp="1djz9n7ii4zia" data-mpi={index}>
                    <div className="bg-cyan-50 p-3 rounded-xl h-fit shrink-0" data-id="element-2287" data-mp="1136nkisy4w3e">
                      <course.icon className="w-6 h-6 text-cyan-600" data-id="element-2288" data-mp="w2iuzsp2k772" />
                    </div>
                    <div data-id="element-2289" data-mp="r4h1edu0ophc">
                      <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2290" data-mp="r35yshcb5nrt">
                        {course.title}
                      </h4>
                      <p className="text-gray-600" data-id="element-2291" data-mp="n46yja1iuf6p">{course.description}</p>
                    </div>
                  </div>)}
              </div>
              <a href="https://wa.me/972522632784" target="_blank" rel="noopener noreferrer" className="mt-8 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white text-lg font-bold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-3 shadow-md" data-id="element-2292" data-mp="ddcbw4kd5dkq">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" data-id="element-2293" data-mp="3kx21ndg6n5fk">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2294" data-mp="3pxg9audvgdd2" />
                </svg>
                לתיאום שיעור
              </a>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2295" data-mp="mkxfj9zg6tpr">
              <div className="bg-[#0A5F8F] rounded-3xl p-8 md:p-10 text-white h-full relative overflow-hidden" data-id="element-2296" data-mp="rkjrlg3ji3wz">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2297" data-mp="27y0glub5k5fp" />
                <div className="relative z-10" data-id="element-2298" data-mp="22yxcqd6ws59b">
                  <h3 className="text-2xl font-bold mb-8" data-id="element-2299" data-mp="cilbasmtow34">מה כולל המועדון?</h3>
                  <ul className="space-y-5" data-id="element-2300" data-mp="ckobg5how5lk">
                    {clubFeatures.map((feature, index) => <li key={index} className="flex items-center gap-4" data-id="element-2301" data-mp="mgiyrdwuyouo" data-mpi={index}>
                        <div className="bg-white/10 p-2 rounded-lg shrink-0" data-id="element-2302" data-mp="mf7yvp6ja18u">
                          <feature.icon className="w-5 h-5 text-cyan-300" data-id="element-2303" data-mp="qiw80catd4w7" />
                        </div>
                        <span className="text-lg text-blue-50" data-id="element-2304" data-mp="vgo6jp5yrp9d">
                          {feature.title}
                        </span>
                      </li>)}
                  </ul>
                  <div className="mt-10 pt-8 border-t border-white/20" data-id="element-2305" data-mp="157sl4nrq983o">
                    <button className="w-full bg-white text-[#0A5F8F] hover:bg-blue-50 text-lg py-4 px-6 rounded-xl font-bold transition-colors" onClick={() => document.getElementById('contact')?.scrollIntoView({
                    behavior: 'smooth'
                  })} data-id="element-2306" data-mp="1a5287wrn97oc">
                      לתיאום שיעור
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50" data-id="element-2307" data-mp="2pq35uksvf67h">
        <div className="container mx-auto px-4" data-id="element-2308" data-mp="3mfty0ddowha4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2309" data-mp="3meiu8dk8v9j0">
            <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-600 px-4 py-2 rounded-full font-bold mb-4" data-id="element-2310" data-mp="38lt5409f5oho">
              <Sailboat className="w-5 h-5" data-id="element-2311" data-mp="57gp3ftntxgo" />
              <span data-id="element-2312" data-mp="55m5c4u4kbe6">המקום המושלם</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-2313" data-mp="o3p8dzxqxnfk">
              למה חוף מעגן מושלם לספורט רוח?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-2314" data-mp="t20mimv2qk3l">
              תנאים אידיאליים לווינג פויל וגלישת רוח לכל הרמות
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2315" data-mp="jbxk5w22btsl">
            {spotFeatures.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-lg border border-cyan-100" data-id="element-2316" data-mp="5bmpsj1jnx2o" data-mpi={index}>
                <div className="bg-cyan-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-4" data-id="element-2317" data-mp="3f9woqbl5yasz">
                  <feature.icon className="w-7 h-7 text-cyan-600" data-id="element-2318" data-mp="3b8bdeuoa24kw" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2319" data-mp="3b6ib1rkp66ip">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed" data-id="element-2320" data-mp="3k8hoyb0f0uhf">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-[#0A5F8F] to-[#0D7AB8] text-white" data-id="element-2321" data-mp="3txri7u6zwj74">
        <div className="container mx-auto px-4" data-id="element-2322" data-mp="btg5lbfbxosr">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto text-center" data-id="element-2323" data-mp="br3p72p7zmht">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-2324" data-mp="1nq20d4147vxr">
              מוכנים לעוף מעל המים?
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed" data-id="element-2325" data-mp="1rqlxw8plp1kf">
              הצטרפו לקהילת הווינג פויל והגלישה שלנו
              <br data-id="element-2326" data-mp="1winf891mlihb" />
              נלמד אתכם לנצל כל רוח!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-2327" data-mp="21g5gdd9eiter">
              <Button className="bg-white text-[#0A5F8F] hover:bg-blue-50 text-lg py-6 px-8" onClick={() => navigate('/wingfoil')} data-id="element-2328" data-mp="14rpsdllxws3r">
                למידע על Wing Foil
              </Button>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg py-6 px-8" onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-2329" data-mp="1dqb4oz0tjaw3">
                צור קשר
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <ContactSection data-id="element-2330" data-mp="dj64uitc6gip" />
      <Footer data-id="element-2331" data-mp="djxznrf40a7x" />
    </div>;
}